import React, { useState } from 'react';
import { 
  UserCog, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Shield,
  Lock
} from 'lucide-react';
import { db, collection, doc, setDoc } from '../../firebase';
import { 
  OUPermissionSelector, 
  ClassGroupPermissionSelector, 
  ReservationPermissionsSelector 
} from '../PermissionSelectors';
import styles from '../CSS/CreateRoleForm.module.css';

const CreateRoleForm = ({ 
  availablePermissions = [], // ✅ Valor por defecto
  onCancel, 
  onSuccess, 
  showFeedback, 
  editingRole = null,
  isModal = false,
  currentUser = null,
  organizationalUnits = []
}) => {
  // ========== 1. TODOS LOS HOOKS PRIMERO ==========
  const [formData, setFormData] = useState({
    id: editingRole?.id || '',
    name: editingRole?.name || '',
    description: editingRole?.description || '',
    permissions: editingRole?.permissions || [],
    emailPermissions: editingRole?.emailPermissions || {
      allowedOUs: [],
      canManageAll: false
    },
    classPermissions: editingRole?.classPermissions || {
      allowedGroups: [],
      canManageAll: false
    },
    reservationPermissions: editingRole?.reservationPermissions || {
      advancedPermissions: []
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // ========== 2. VARIABLES Y CONSTANTES ==========
  const isEditingOwnRole = editingRole && currentUser && editingRole.id === currentUser.role;

  const LIMITS = {
    id: 20,
    name: 50,
    description: 300
  };

  // ========== 3. FUNCIONES ==========
  const validateForm = () => {
    const errors = {};
    
    if (!editingRole && !formData.id) {
      errors.id = 'El ID del rol es requerido';
    } else if (!editingRole && !/^[a-z0-9_]+$/.test(formData.id)) {
      errors.id = 'El ID solo puede contener letras minúsculas, números y guiones bajos';
    } else if (!editingRole && formData.id.length > LIMITS.id) {
      errors.id = `El ID no puede exceder ${LIMITS.id} caracteres`;
    }
    
    if (!formData.name) {
      errors.name = 'El nombre del rol es requerido';
    } else if (formData.name.length > LIMITS.name) {
      errors.name = `El nombre no puede exceder ${LIMITS.name} caracteres`;
    }
    
    if (formData.description.length > LIMITS.description) {
      errors.description = `La descripción no puede exceder ${LIMITS.description} caracteres`;
    }
    
    if (formData.permissions.length === 0) {
      errors.permissions = 'Debe seleccionar al menos un permiso';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePermissionChange = (permissionId) => {
    if (isEditingOwnRole) {
      showFeedback("error", "No puedes modificar los permisos de tu propio rol");
      return;
    }

    const newPermissions = formData.permissions.includes(permissionId)
      ? formData.permissions.filter(p => p !== permissionId)
      : [...formData.permissions, permissionId];

    setFormData(prev => ({
      ...prev,
      permissions: newPermissions,
      emailPermissions: permissionId === 'correos' && !newPermissions.includes('correos')
        ? { allowedOUs: [], canManageAll: false }
        : prev.emailPermissions,
      classPermissions: permissionId === 'clases' && !newPermissions.includes('clases')
        ? { allowedGroups: [], canManageAll: false }
        : prev.classPermissions,
      reservationPermissions: permissionId === 'reservas' && !newPermissions.includes('reservas')
        ? { advancedPermissions: [] }
        : prev.reservationPermissions
    }));
    
    if (validationErrors.permissions && !formData.permissions.includes(permissionId)) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.permissions;
        return newErrors;
      });
    }
  };

  const handleOUChange = (selectedOUs) => {
    const countAllOUs = (ous) => {
      let count = 0;
      ous.forEach(ou => {
        count++;
        if (ou.children && ou.children.length > 0) {
          count += countAllOUs(ou.children);
        }
      });
      return count;
    };
    
    const totalOUs = countAllOUs(organizationalUnits);
    
    setFormData(prev => ({
      ...prev,
      emailPermissions: {
        ...prev.emailPermissions,
        allowedOUs: selectedOUs,
        canManageAll: selectedOUs.length > 0 && selectedOUs.length === totalOUs
      }
    }));
  };

  const handleClassGroupChange = (selectedGroups) => {
    const totalGroups = 3;
    
    setFormData(prev => ({
      ...prev,
      classPermissions: {
        ...prev.classPermissions,
        allowedGroups: selectedGroups,
        canManageAll: selectedGroups.length > 0 && selectedGroups.length === totalGroups
      }
    }));
  };

  const handleReservationPermissionsChange = (selectedPermissions) => {
    setFormData(prev => ({
      ...prev,
      reservationPermissions: {
        advancedPermissions: selectedPermissions
      }
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      console.log('❌ Errores de validación:', validationErrors);
      showFeedback("error", "Por favor, corrija los errores en el formulario");
      return;
    }

    setLoading(true);
    
    try {
      const roleId = editingRole ? editingRole.id : formData.id;
      const roleDoc = doc(collection(db, "roles"), roleId);
      
      const roleData = isEditingOwnRole ? {
        name: formData.name.trim(),
        description: formData.description.trim(),
        permissions: editingRole.permissions,
        emailPermissions: editingRole.emailPermissions,
        classPermissions: editingRole.classPermissions,
        reservationPermissions: editingRole.reservationPermissions,
        updatedAt: new Date().toISOString()
      } : {
        name: formData.name.trim(),
        description: formData.description.trim(),
        permissions: formData.permissions,
        ...(formData.permissions.includes('correos') && {
          emailPermissions: formData.emailPermissions
        }),
        ...(formData.permissions.includes('clases') && {
          classPermissions: formData.classPermissions
        }),
        ...(formData.permissions.includes('reservas') && {
          reservationPermissions: formData.reservationPermissions
        }),
        ...(editingRole ? 
          { updatedAt: new Date().toISOString() } : 
          { createdAt: new Date().toISOString() }
        )
      };

      console.log('💾 Guardando rol:', roleData);
      await setDoc(roleDoc, roleData, editingRole ? { merge: true } : {});
      console.log('✅ Rol guardado exitosamente');
      onSuccess();
    } catch (error) {
      console.error('❌ Error guardando rol:', error);
      showFeedback("error", `Error al ${editingRole ? 'actualizar' : 'crear'} rol: ` + error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateIdFromName = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .substring(0, LIMITS.id);
  };

  const handleInputChange = (field, value) => {
    if (field === 'id' && value.length > LIMITS.id) return;
    if (field === 'name' && value.length > LIMITS.name) return;
    if (field === 'description' && value.length > LIMITS.description) return;

    const updates = { [field]: value };

    if (field === 'name' && !editingRole) {
      updates.id = generateIdFromName(value);
    }

    setFormData(prev => ({ ...prev, ...updates }));

    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const getPermissionIcon = (permissionId) => {
    const icons = {
      'gestion': '⚙️',
      'correos': '📧',
      'clases': '📚',
      'reservas': '📅',
      'incidencias': '🎫',
      'admin': '👑'
    };
    return icons[permissionId] || '🔐';
  };

  const getPermissionColor = (permissionId) => {
    const colors = {
      'gestion': '#9f7aea',
      'correos': '#ed8936',
      'clases': '#38a169',
      'reservas': '#3182ce',
      'incidencias': '#e53e3e',
      'admin': '#d69e2e'
    };
    return colors[permissionId] || '#6b7280';
  };

  const getCharacterCount = (field) => {
    const current = formData[field]?.length || 0;
    const limit = LIMITS[field];
    const isNearLimit = current / limit > 0.8;
    const isAtLimit = current >= limit;
    
    return {
      current,
      limit,
      isNearLimit,
      isAtLimit,
      remaining: limit - current
    };
  };

  // ========== 4. VARIABLES DERIVADAS ==========
  const containerClass = isModal ? `${styles.container} ${styles.modalContainer}` : styles.container;
  const hasEmailPermission = formData.permissions.includes('correos');
  const hasClassPermission = formData.permissions.includes('clases');
  const hasReservationPermission = formData.permissions.includes('reservas');

  // ========== 5. VALIDACIÓN (DESPUÉS DE TODOS LOS HOOKS) ==========
  if (!availablePermissions || availablePermissions.length === 0) {
    return (
      <div className={containerClass}>
        <div className={styles.error}>
          <AlertCircle size={20} />
          <p>No hay permisos disponibles. Por favor, contacta al administrador.</p>
        </div>
      </div>
    );
  }

  // ========== 6. RENDER PRINCIPAL ==========
  return (
    <div className={containerClass}>
      {!isModal && (
        <div className={styles.header}>
          <h3 className={styles.title}>
            <UserCog size={24} />
            {editingRole ? `Editar Rol: ${editingRole.name}` : 'Crear Nuevo Rol'}
          </h3>
          <p className={styles.subtitle}>
            {editingRole ? 
              'Modifica los datos del rol seleccionado' : 
              'Define un nuevo rol con permisos específicos para los usuarios'
            }
          </p>
        </div>
      )}

      {isEditingOwnRole && (
        <div className={styles.ownRoleWarning}>
          <div className={styles.warningContent}>
            <Lock size={20} />
            <div>
              <h4>Editando tu propio rol</h4>
              <p>Solo puedes modificar el nombre y la descripción. Los permisos están protegidos para evitar que te bloquees el acceso.</p>
            </div>
          </div>
        </div>
      )}

      <div className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            <UserCog size={16} />
            Nombre del Rol
          </label>
          <div className={styles.inputContainer}>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="ej: Profesor de Matemáticas"
              className={`${styles.input} ${validationErrors.name ? styles.inputError : ''}`}
              maxLength={LIMITS.name}
            />
            <div className={styles.characterCount}>
              <span className={`${styles.count} ${getCharacterCount('name').isNearLimit ? styles.countWarning : ''} ${getCharacterCount('name').isAtLimit ? styles.countError : ''}`}>
                {getCharacterCount('name').current}/{LIMITS.name}
              </span>
            </div>
          </div>
          {validationErrors.name && (
            <span className={styles.error}>
              <AlertCircle size={14} />
              {validationErrors.name}
            </span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Descripción (opcional)
          </label>
          <div className={styles.textareaContainer}>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Descripción detallada del rol y sus responsabilidades..."
              className={`${styles.textarea} ${validationErrors.description ? styles.inputError : ''}`}
              rows={3}
              maxLength={LIMITS.description}
            />
            <div className={styles.characterCount}>
              <span className={`${styles.count} ${getCharacterCount('description').isNearLimit ? styles.countWarning : ''} ${getCharacterCount('description').isAtLimit ? styles.countError : ''}`}>
                {getCharacterCount('description').current}/{LIMITS.description}
              </span>
            </div>
          </div>
          {validationErrors.description && (
            <span className={styles.error}>
              <AlertCircle size={14} />
              {validationErrors.description}
            </span>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            <Shield size={16} />
            Permisos del Rol
            {isEditingOwnRole && (
              <span className={styles.lockedLabel}>
                <Lock size={14} />
                Bloqueado
              </span>
            )}
          </label>
          <p className={styles.helpText}>
            {isEditingOwnRole ? 
              'Los permisos de tu propio rol están protegidos y no pueden ser modificados' :
              'Selecciona los permisos que tendrán los usuarios con este rol'
            }
          </p>
          
          <div className={`${styles.permissionsGrid} ${isEditingOwnRole ? styles.permissionsDisabled : ''}`}>
            {availablePermissions?.map(permission => {
              const isSelected = formData.permissions.includes(permission.id);
              const permissionColor = getPermissionColor(permission.id);
              
              return (
                <div 
                  key={permission.id} 
                  className={`${styles.permissionCard} ${isSelected ? styles.permissionSelected : ''} ${isEditingOwnRole ? styles.permissionDisabled : ''}`}
                  onClick={() => handlePermissionChange(permission.id)}
                  style={{
                    borderLeftColor: permissionColor,
                    ...(isSelected && {
                      backgroundColor: `${permissionColor}08`,
                      borderColor: `${permissionColor}40`
                    }),
                    ...(isEditingOwnRole && {
                      opacity: 0.7,
                      cursor: 'not-allowed'
                    })
                  }}
                >
                  <div className={styles.permissionHeader}>
                    <div className={styles.permissionIconContainer}>
                      <span className={styles.permissionIcon}>
                        {getPermissionIcon(permission.id)}
                      </span>
                      {isEditingOwnRole && (
                        <Lock size={12} className={styles.permissionLockIcon} />
                      )}
                    </div>
                    
                    <div className={styles.permissionInfo}>
                      <h4 className={styles.permissionName}>{permission.name}</h4>
                      <p className={styles.permissionDescription}>
                        {permission.description}
                      </p>
                    </div>
                    
                    <div className={styles.toggleContainer}>
                      <label className={styles.toggleLabel} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handlePermissionChange(permission.id)}
                          className={styles.toggleInput}
                          disabled={isEditingOwnRole}
                        />
                        <span 
                          className={`${styles.toggleSlider} ${isSelected ? styles.toggleActive : ''} ${isEditingOwnRole ? styles.toggleDisabled : ''}`}
                          style={{
                            ...(isSelected && !isEditingOwnRole && {
                              backgroundColor: permissionColor
                            })
                          }}
                        >
                          <span className={styles.toggleThumb}></span>
                        </span>
                      </label>
                      <span className={styles.toggleStatus}>
                        {isEditingOwnRole ? 'Bloqueado' : (isSelected ? 'Activado' : 'Desactivado')}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {validationErrors.permissions && (
            <span className={styles.error}>
              <AlertCircle size={14} />
              {validationErrors.permissions}
            </span>
          )}
        </div>

        {hasEmailPermission && !isEditingOwnRole && (
          <OUPermissionSelector
            organizationalUnits={organizationalUnits}
            selectedOUs={formData.emailPermissions.allowedOUs}
            onChange={handleOUChange}
          />
        )}

        {hasClassPermission && !isEditingOwnRole && (
          <ClassGroupPermissionSelector
            selectedGroups={formData.classPermissions.allowedGroups}
            onChange={handleClassGroupChange}
          />
        )}

        {hasReservationPermission && !isEditingOwnRole && (
          <ReservationPermissionsSelector
            selectedPermissions={formData.reservationPermissions.advancedPermissions}
            onChange={handleReservationPermissionsChange}
          />
        )}

        <div className={styles.summary}>
          <h4 className={styles.summaryTitle}>📋 Resumen del Rol</h4>
          <div className={styles.summaryContent}>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryItem}>
                <strong>Nombre:</strong>
                <span className={styles.summaryValue}>
                  {formData.name || 'Sin definir'}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <strong>Permisos:</strong>
                <span className={styles.summaryValue}>
                  {formData.permissions.length} de {availablePermissions?.length || 0} activados
                  {isEditingOwnRole && (
                    <span className={styles.protectedIndicator}>
                      <Lock size={12} />
                      Protegidos
                    </span>
                  )}
                </span>
              </div>
              {hasEmailPermission && (
                <div className={styles.summaryItem}>
                  <strong>OUs de Correos:</strong>
                  <span className={styles.summaryValue}>
                    {formData.emailPermissions.canManageAll ? 
                      'Todas las unidades' : 
                      `${formData.emailPermissions.allowedOUs.length} unidades`
                    }
                  </span>
                </div>
              )}
              {hasClassPermission && (
                <div className={styles.summaryItem}>
                  <strong>Grupos de Clases:</strong>
                  <span className={styles.summaryValue}>
                    {formData.classPermissions.canManageAll ? 
                      'Todos los grupos' : 
                      `${formData.classPermissions.allowedGroups.length} grupos`
                    }
                  </span>
                </div>
              )}
              {hasReservationPermission && (
                <div className={styles.summaryItem}>
                  <strong>Permisos de Reservas:</strong>
                  <span className={styles.summaryValue}>
                    {formData.reservationPermissions.advancedPermissions.length === 0 ? 
                      'Permisos básicos' : 
                      `${formData.reservationPermissions.advancedPermissions.length} permisos avanzados`
                    }
                  </span>
                </div>
              )}
            </div>
            
            {formData.permissions.length > 0 && (
              <div className={styles.permissionTagsContainer}>
                <div className={styles.permissionTags}>
                  {formData.permissions.map(permissionId => {
                    const permission = availablePermissions?.find(p => p.id === permissionId);
                    const color = getPermissionColor(permissionId);
                    return (
                      <span 
                        key={permissionId} 
                        className={`${styles.permissionTag} ${isEditingOwnRole ? styles.permissionTagProtected : ''}`}
                        style={{
                          backgroundColor: `${color}20`,
                          color: color,
                          borderColor: `${color}40`
                        }}
                      >
                        <span className={styles.tagIcon}>{getPermissionIcon(permissionId)}</span>
                        <span className={styles.tagText}>{permission?.name || permissionId}</span>
                        {isEditingOwnRole && <Lock size={10} />}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            onClick={onCancel}
            className={`${styles.button} ${styles.secondaryButton}`}
            disabled={loading}
          >
            <XCircle size={16} />
            <span className={styles.buttonText}>Cancelar</span>
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className={`${styles.button} ${styles.primaryButton} ${loading ? styles.loadingButton : ''}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className={styles.spinner}></div>
                <span className={styles.buttonText}>Guardando...</span>
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                <span className={styles.buttonText}>
                  {editingRole ? 'Actualizar Rol' : 'Crear Rol'}
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateRoleForm;